import { Link } from "react-router-dom";
import { ExternalLink, BookOpen, ShieldCheck } from "lucide-react";
import { RESEARCH_DATA } from "../lib/researchData";

const VERIFIED_DATE = "February 2026";

export default function Research() {
  const totalPapers = RESEARCH_DATA.reduce((acc, p) => acc + p.papers.length, 0);

  return (
    <div data-testid="research-page" className="animate-in fade-in duration-500">
      <section className="px-6 md:px-12 lg:px-24 pt-20 pb-12 border-b border-border max-w-5xl">
        <p className="overline text-primary mb-5">Research library</p>
        <h1 className="editorial-heading text-5xl md:text-7xl text-secondary leading-[0.95] mb-6">
          The science <em className="italic text-primary">behind the catalog.</em>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl">
          A curated index of peer-reviewed literature for each peptide in our catalog. References
          are drawn from PubMed-indexed journals and are provided for research reference only.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <span data-testid="research-stats-peptides">
            <strong className="text-secondary">{RESEARCH_DATA.length}</strong> peptides
          </span>
          <span data-testid="research-stats-papers">
            <strong className="text-secondary">{totalPapers}</strong> references
          </span>
          <span
            data-testid="research-verified-stamp"
            className="inline-flex items-center gap-2 text-primary"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Verified against PubMed · {VERIFIED_DATE}
          </span>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 max-w-5xl">
        <div className="bg-muted border-l-2 border-primary p-5 mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-secondary font-semibold mb-2">
            Research reference only
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            These citations are provided as scientific reference material. The literature does not
            constitute medical advice or a recommendation for human use. RegenX Wellness sells
            peptides strictly for in-vitro laboratory research.
          </p>
        </div>

        <div className="space-y-16">
          {RESEARCH_DATA.map((peptide) => (
            <article
              key={peptide.slug}
              data-testid={`research-${peptide.slug}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-border pt-10"
            >
              <div className="md:col-span-4">
                <p className="overline text-primary mb-3">
                  {peptide.papers.length} {peptide.papers.length === 1 ? "paper" : "papers"}
                </p>
                <h2 className="editorial-heading text-3xl md:text-4xl text-secondary mb-4">
                  {peptide.name}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {peptide.summary}
                </p>
              </div>
              <div className="md:col-span-8">
                <ul className="space-y-6">
                  {peptide.papers.map((paper, i) => (
                    <li
                      key={i}
                      data-testid={`paper-${peptide.slug}-${i}`}
                      className="border-l border-border pl-5 hover:border-primary transition-colors"
                    >
                      {paper.component && (
                        <p className="overline text-primary mb-1.5">{paper.component}</p>
                      )}
                      <p className="text-sm text-muted-foreground italic mb-1">
                        {paper.authors}
                      </p>
                      <p className="text-base text-secondary leading-snug mb-2 font-medium">
                        {paper.title}
                      </p>
                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-3">
                        {paper.journal} &middot; {paper.year} &middot; {paper.ref}
                      </p>
                      {paper.pmid && (
                        <a
                          href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`paper-link-${peptide.slug}-${i}`}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-secondary hover:text-primary transition-colors border-b border-secondary hover:border-primary pb-0.5"
                        >
                          <BookOpen className="h-3 w-3" />
                          PubMed PMID {paper.pmid}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-20 pt-10 border-t border-border space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-secondary">Want a citation we&rsquo;ve missed?</strong>{" "}
            Email <a href="mailto:info@regenxwellness.com.au" className="text-secondary underline underline-offset-4 hover:text-primary">info@regenxwellness.com.au</a> with the reference and we&rsquo;ll add it.
          </p>
          <Link
            to="/"
            className="inline-block text-xs uppercase tracking-[0.22em] text-secondary hover:text-primary border-b border-secondary pb-1"
            data-testid="research-back-home"
          >
            ← Back home
          </Link>
        </div>
      </section>
    </div>
  );
}
